import React, { useState, useEffect } from 'react';
import { counsellorService } from '../../services/authService';
import './CounsellorDashboard.css';

const CounsellorDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const data = await counsellorService.getCounsellorDashboard();
            setDashboardData(data);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to fetch dashboard data');
            console.error('Dashboard fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="spinner"></div>
                <p>Loading your dashboard...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-error">
                <div className="error-content">
                    <i className="error-icon fas fa-exclamation-circle"></i>
                    <h2>Error Loading Dashboard</h2>
                    <p>{error}</p>
                    <div className="error-actions">
                        <button className="retry-button" onClick={fetchDashboardData}>
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="counsellor-dashboard">
            {/* Profile Section */}
            <div className="dashboard-header">
                <div className="profile-summary">
                    <h1>Welcome, {dashboardData.profile.name}</h1>
                    <p className="counsellor-info">
                        <span>{dashboardData.profile.specialization}</span> • 
                        <span>{dashboardData.profile.experience} Years Experience</span>
                    </p>
                </div>
                <div className="dashboard-tabs">
                    <button 
                        className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => handleTabChange('overview')}
                    >
                        Overview
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'sessions' ? 'active' : ''}`}
                        onClick={() => handleTabChange('sessions')}
                    >
                        Sessions
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'students' ? 'active' : ''}`}
                        onClick={() => handleTabChange('students')}
                    >
                        Students
                    </button>
                </div>
            </div>

            {activeTab === 'overview' && (
                <>
                    {/* Statistics Section */}
                    <div className="stats-grid">
                        <div className="stat-card">
                            <h3>Total Sessions</h3>
                            <p className="stat-number">{dashboardData.stats.totalSessions}</p>
                            <div className="stat-trend">
                                <span className={`trend-indicator ${dashboardData.stats.sessionTrend > 0 ? 'positive' : 'negative'}`}>
                                    <i className={`fas fa-arrow-${dashboardData.stats.sessionTrend > 0 ? 'up' : 'down'}`}></i>
                                    {Math.abs(dashboardData.stats.sessionTrend)}%
                                </span>
                                <span className="trend-period">vs last month</span>
                            </div>
                        </div>
                        <div className="stat-card">
                            <h3>Active Students</h3>
                            <p className="stat-number">{dashboardData.stats.activeStudents}</p>
                            <div className="stat-trend">
                                <span className="trend-indicator neutral">
                                    <i className="fas fa-minus"></i>
                                    No change
                                </span>
                            </div>
                        </div>
                        <div className="stat-card">
                            <h3>Completed Sessions</h3>
                            <p className="stat-number">{dashboardData.stats.completedSessions}</p>
                            <div className="stat-trend">
                                <span className={`trend-indicator ${dashboardData.stats.completedTrend > 0 ? 'positive' : 'negative'}`}>
                                    <i className={`fas fa-arrow-${dashboardData.stats.completedTrend > 0 ? 'up' : 'down'}`}></i>
                                    {Math.abs(dashboardData.stats.completedTrend)}%
                                </span>
                                <span className="trend-period">vs last month</span>
                            </div>
                        </div>
                        <div className="stat-card">
                            <h3>Average Rating</h3>
                            <p className="stat-number">{dashboardData.stats.averageRating.toFixed(1)}</p>
                            <div className="rating-stars">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <i 
                                        key={star}
                                        className={`fas fa-star ${star <= dashboardData.stats.averageRating ? 'filled' : ''}`}
                                    ></i>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Recent Activities Section */}
                    <div className="dashboard-section">
                        <h2>Recent Activities</h2>
                        <div className="activities-list">
                            {dashboardData.recentActivities.map(activity => (
                                <div key={activity.id} className="activity-card">
                                    <div className="activity-icon">
                                        <i className={`fas fa-${getActivityIcon(activity.type)}`}></i>
                                    </div>
                                    <div className="activity-details">
                                        <p>{activity.description}</p>
                                        <div className="activity-meta">
                                            <span className="activity-date">
                                                {new Date(activity.date).toLocaleDateString()}
                                            </span>
                                            {activity.student && (
                                                <span className="activity-student">
                                                    <i className="fas fa-user"></i>
                                                    {activity.student}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'sessions' && (
                <div className="dashboard-section">
                    <div className="section-header">
                        <h2>Upcoming Sessions</h2>
                        <div className="section-actions">
                            <div className="search-box">
                                <i className="fas fa-search"></i>
                                <input 
                                    type="text" 
                                    placeholder="Search sessions..."
                                />
                            </div>
                            <button className="schedule-btn">
                                <i className="fas fa-plus"></i>
                                Schedule Session
                            </button>
                        </div>
                    </div>
                    <div className="sessions-grid">
                        {dashboardData.upcomingSessions.map(session => (
                            <div key={session.id} className="session-card">
                                <div className="session-header">
                                    <div className="session-info">
                                        <h3>{session.studentName}</h3>
                                        <span className={`status-badge ${session.status.toLowerCase()}`}>
                                            {session.status}
                                        </span>
                                    </div>
                                    <div className="session-actions">
                                        <button className="action-btn" title="Edit">
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button className="action-btn" title="More">
                                            <i className="fas fa-ellipsis-v"></i>
                                        </button>
                                    </div>
                                </div>
                                <div className="session-details">
                                    <p>
                                        <i className="fas fa-calendar"></i>
                                        {new Date(session.date).toLocaleDateString()}
                                    </p>
                                    <p>
                                        <i className="fas fa-clock"></i>
                                        {session.time}
                                    </p>
                                    <p>
                                        <i className="fas fa-tag"></i>
                                        {session.type}
                                    </p>
                                    {session.notes && (
                                        <p className="session-notes">
                                            <i className="fas fa-sticky-note"></i>
                                            {session.notes}
                                        </p>
                                    )}
                                </div>
                                <div className="session-footer">
                                    <button className="join-session-btn">
                                        <i className="fas fa-video"></i>
                                        Join Session
                                    </button>
                                    <button className="reschedule-btn">
                                        Reschedule
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {activeTab === 'students' && (
                <div className="dashboard-section">
                    <div className="section-header">
                        <h2>Your Students</h2>
                        <div className="section-actions">
                            <div className="search-box">
                                <i className="fas fa-search"></i>
                                <input 
                                    type="text" 
                                    placeholder="Search students..."
                                />
                            </div>
                            <div className="filter-dropdown">
                                <button className="filter-btn">
                                    <i className="fas fa-filter"></i>
                                    Filter
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="students-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Student Name</th>
                                    <th>Last Session</th>
                                    <th>Total Sessions</th>
                                    <th>Progress</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dashboardData.students.map(student => (
                                    <tr key={student.id}>
                                        <td>
                                            <div className="student-info">
                                                <img 
                                                    src={student.avatar || '/default-avatar.png'} 
                                                    alt={student.name}
                                                    className="student-avatar"
                                                />
                                                <div className="student-details">
                                                    <span className="student-name">{student.name}</span>
                                                    <span className="student-email">{student.email}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{new Date(student.lastSession).toLocaleDateString()}</td>
                                        <td>{student.totalSessions}</td>
                                        <td>
                                            <div className="progress-bar">
                                                <div 
                                                    className="progress" 
                                                    style={{ width: `${student.progress}%` }}
                                                ></div>
                                                <span>{student.progress}%</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${student.status.toLowerCase()}`}>
                                                {student.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="row-actions">
                                                <button className="action-btn" title="View Profile">
                                                    <i className="fas fa-user"></i>
                                                </button>
                                                <button className="action-btn" title="Schedule Session">
                                                    <i className="fas fa-calendar-plus"></i>
                                                </button>
                                                <button className="action-btn" title="Message">
                                                    <i className="fas fa-comment"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper function to get activity icon based on type
const getActivityIcon = (type) => {
    switch (type) {
        case 'session_scheduled':
            return 'calendar-plus';
        case 'session_completed':
            return 'check-circle';
        case 'session_cancelled':
            return 'calendar-times';
        case 'student_feedback':
            return 'comment';
        case 'profile_updated':
            return 'user-edit';
        case 'note_added':
            return 'sticky-note';
        default:
            return 'bell';
    }
};

export default CounsellorDashboard; 